/**
 * Unified Popup Utility
 * 
 * This utility provides helper functions for creating popups using the unified popup system.
 * Import this file where you need to create popups programmatically.
 */

/**
 * Creates a popup notification
 * @param {string} messageText - The message to display
 * @param {string} type - The type of popup ('success', 'error', 'warning', 'info')
 * @param {Object} options - Additional options
 * @param {boolean} options.gradient - Whether to use gradient styling
 * @param {number} options.duration - Auto-close duration in milliseconds (0 = no auto-close)
 * @returns {HTMLElement} The created popup element
 */
export const createPopup = (messageText, type = 'success', options = {}) => {
  const { gradient = false, duration = 3000 } = options;
  
  const popup = document.createElement('div');
  let className = 'popup';
  
  if (gradient) {
    className += ' popup--gradient';
  }
  
  if (type) {
    className += ` popup--${type}`;
  }
  
  popup.className = className;

  const closeBtn = document.createElement('button');
  closeBtn.className = 'popup-close';
  closeBtn.innerHTML = '&times;';
  closeBtn.onclick = () => popup.remove();

  const message = document.createElement('div');
  message.className = 'popup-message';
  message.textContent = messageText;

  popup.appendChild(closeBtn);
  popup.appendChild(message);
  document.body.appendChild(popup);

  if (duration > 0) {
    setTimeout(() => {
      if (popup.parentNode) {
        popup.remove();
      }
    }, duration);
  }

  return popup;
};

/**
 * Creates a mobile popup overlay
 * @param {string} title - The popup title
 * @param {HTMLElement|string} content - The popup content (HTML element or string)
 * @param {Function} onClose - Callback function when popup is closed
 * @returns {HTMLElement} The created popup overlay element
 */
export const createMobilePopup = (title, content, onClose = null) => {
  const overlay = document.createElement('div');
  overlay.className = 'mobile-popup-overlay';

  const popup = document.createElement('div');
  popup.className = 'mobile-popup';

  const header = document.createElement('div');
  header.className = 'popup-header';

  const titleElement = document.createElement('h3');
  titleElement.textContent = title;

  const closeBtn = document.createElement('button');
  closeBtn.className = 'messages-popup-close';
  closeBtn.innerHTML = '&times;';
  closeBtn.onclick = () => {
    overlay.remove();
    if (onClose) onClose();
  };

  header.appendChild(titleElement);
  header.appendChild(closeBtn);

  const scrollArea = document.createElement('div');
  scrollArea.className = 'popup-scroll';

  if (typeof content === 'string') {
    scrollArea.innerHTML = content;
  } else if (content instanceof HTMLElement) {
    scrollArea.appendChild(content);
  }

  popup.appendChild(header);
  popup.appendChild(scrollArea);
  overlay.appendChild(popup);
  document.body.appendChild(overlay);

  // Close on overlay click
  overlay.onclick = (e) => {
    if (e.target === overlay) {
      overlay.remove();
      if (onClose) onClose();
    }
  };

  return overlay;
};

/**
 * Creates a simple success popup
 * @param {string} message - Success message
 * @param {boolean} gradient - Whether to use gradient styling
 */
export const showSuccess = (message, gradient = false) => {
  return createPopup(message, 'success', { gradient });
};

/**
 * Creates a simple error popup
 * @param {string} message - Error message
 * @param {boolean} gradient - Whether to use gradient styling
 */
export const showError = (message, gradient = false) => {
  return createPopup(message, 'error', { gradient });
};

/**
 * Creates a simple warning popup
 * @param {string} message - Warning message
 * @param {boolean} gradient - Whether to use gradient styling
 */
export const showWarning = (message, gradient = false) => {
  return createPopup(message, 'warning', { gradient });
};

/**
 * Creates a simple info popup
 * @param {string} message - Info message
 * @param {boolean} gradient - Whether to use gradient styling
 */
export const showInfo = (message, gradient = false) => {
  return createPopup(message, 'info', { gradient });
};

/**
 * Example usage:
 * 
 * import { createPopup, showSuccess, showError, createMobilePopup } from './utils/popupUtils';
 * 
 * // Basic popup
 * createPopup("Hello World!", "success");
 * 
 * // Gradient popup with auto-close disabled
 * createPopup("Important message", "error", { gradient: true, duration: 0 });
 * 
 * // Simple helpers
 * showSuccess("Account created!");
 * showError("Something went wrong!");
 * 
 * // Mobile popup
 * const contentElement = document.createElement('div');
 * contentElement.innerHTML = '<p>Mobile popup content</p>';
 * createMobilePopup("Chat List", contentElement, () => console.log("Popup closed"));
 */